<template>
  <nav class="navbar navbar-expand-lg">
    <!-- 移动端菜单按钮 -->
    <button 
      class="navbar-toggler mobile-menu-btn" 
      type="button" 
      @click="toggleMenu"
      :aria-expanded="isMenuOpen"
      aria-controls="navbarNav"
      aria-label="切换导航"
    >
      <span class="navbar-toggler-icon" :class="{ 'open': isMenuOpen }">
        <span></span>
        <span></span>
        <span></span>
      </span>
    </button>
    
    <!-- 导航菜单 -->
    <div 
      class="collapse navbar-collapse" 
      :class="{ 'show': isMenuOpen }" 
      id="navbarNav"
    >
      <ul class="navbar-nav">
        <li v-for="(item, index) in navItems" :key="index" class="nav-item">
          <!-- 桌面端：有子菜单的项 -->
          <div v-if="item.children" class="dropdown d-none d-lg-block">
            <a
              class="nav-link dropdown-toggle"
              href="javascript:void(0)"
              :class="{ 
                'active': isActive(item.path) || isChildActive(item.children)
              }"
              @click="toggleSubMenu(index)"
              role="button"
              :aria-expanded="isSubMenuOpen(index)"
            >
              {{ item.title }}
            </a>
            <ul 
              class="dropdown-menu"
              :class="{ 'show': isSubMenuOpen(index) }"
            >
              <li v-for="(child, childIndex) in item.children" :key="childIndex">
                <router-link
                  class="dropdown-item"
                  :to="child.path"
                  :class="{ 'active': isActive(child.path) }"
                  @click="closeAllMenus"
                >
                  {{ child.title }}
                </router-link>
              </li>
            </ul>
          </div>
          
          <!-- 移动端：有子菜单的项（作为可点击链接） -->
          <router-link 
            v-if="item.children"
            :to="item.path" 
            class="nav-link d-lg-none"
            :class="{ 'active': isActive(item.path) }"
            @click="closeMenu"
          >
            {{ item.title }}
          </router-link>
          
          <!-- 普通菜单项 -->
          <router-link 
            v-else
            :to="item.path" 
            class="nav-link"
            :class="{ 'active': isActive(item.path) }"
            @click="closeMenu"
          >
            {{ item.title }}
          </router-link>
        </li>
      </ul>
    </div>
  </nav>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()
const isMenuOpen = ref(false)
const openSubMenu = ref(null)

const navItems = [
  { path: '/', title: '首页' },
  // { path: '/about', title: '智能体服务' },
  { path: '/service', title: '解决方案' },
  { path: '/case', title: '客户案例' },
  { 
    path: '/technical', 
    title: '技术支持',
    children: [
      { path: '/technical/support', title: '技术支持' },
      { path: '/technical/faq', title: '常见问题' },
      { path: '/technical/device', title: '设备答疑' },
      { path: '/technical/service', title: '售后服务' }
    ]
  },
  { path: '/about', title: '关于我们' },
  { path: '/contact', title: '联系我们' }
]

// 切换主菜单
const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value
  if (!isMenuOpen.value) {
    openSubMenu.value = null
  }
}

// 关闭主菜单
const closeMenu = () => {
  isMenuOpen.value = false
  openSubMenu.value = null
}

// 关闭所有菜单
const closeAllMenus = () => {
  isMenuOpen.value = false
  openSubMenu.value = null
}

// 切换子菜单（仅桌面端）
const toggleSubMenu = (index) => {
  if (window.innerWidth >= 992) { // 仅桌面端生效
    if (openSubMenu.value === index) {
      openSubMenu.value = null
    } else {
      openSubMenu.value = index
    }
  }
}

// 检查子菜单是否打开
const isSubMenuOpen = (index) => {
  return openSubMenu.value === index
}

// 检查路由是否激活
const isActive = (path) => {
  return route.path === path
}

// 检查子路由是否激活
const isChildActive = (children) => {
  return children?.some(child => route.path === child.path)
}

// 点击外部关闭菜单
const handleClickOutside = (event) => {
  const nav = event.target.closest('.navbar')
  if (!nav) {
    closeAllMenus()
  }
}

// 键盘导航支持
const handleKeydown = (event) => {
  if (event.key === 'Escape') {
    closeAllMenus()
  }
}

// 窗口大小变化时重置菜单状态
const handleResize = () => {
  if (window.innerWidth >= 992) {
    isMenuOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleKeydown)
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped>
.navbar {
  padding: 0;
}

.navbar-nav {
  align-items: center;
}

.nav-item {
  margin: 0 0.5rem;
}

.nav-link {
  color: #333 !important;
  font-weight: 500;
  padding: 0.5rem 1rem !important;
  transition: all 0.3s ease;
  border-radius: 0.375rem;
  text-decoration: none;
  display: block;
}

.nav-link:hover,
.nav-link.active {
  color: #0054c6 !important;
  /* background-color: rgba(0, 84, 198, 0.1); */
}

.nav-link.active {
  position: relative;
}

.nav-link.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 1rem;
  right: 1rem;
  height: 2px;
  background: #0054c6;
  border-radius: 1px;
}

/* 下拉菜单样式（仅桌面端） */
.dropdown-menu {
  border: none;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  border-radius: 0.5rem;
  padding: 0.5rem;
  min-width: 200px;
  transition: all 0.3s ease;
}

.dropdown-item {
  padding: 0.625rem 1rem;
  border-radius: 0.375rem;
  color: #333;
  transition: all 0.2s ease;
  text-decoration: none;
  display: block;
}

.dropdown-item.active {
  color: #0054c6;
  background-color: rgba(0, 84, 198, 0.1);
  text-decoration: none;
}
.dropdown-toggle::after{
  margin-left: 0;
  border: none;
}
/* 移动端菜单按钮动画 */
.mobile-menu-btn {
  border: none;
  padding: 0.25rem;
  background: transparent;
  width: 2.5rem;
  height: 2rem;
}

.navbar-toggler-icon {
  background-image: none;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  transition: all 0.3s ease;
}

.navbar-toggler-icon span {
  display: block;
  height: 2px;
  width: 100%;
  background: #333;
  border-radius: 2px;
  transition: all 0.3s ease;
  transform-origin: center;
}

.navbar-toggler-icon.open span:nth-child(1) {
  transform: translateY(11px) rotate(45deg);
}

.navbar-toggler-icon.open span:nth-child(2) {
  opacity: 0;
  transform: scale(0);
}

.navbar-toggler-icon.open span:nth-child(3) {
  transform: translateY(-11px) rotate(-45deg);
}

/* 移动端样式 */
@media (max-width: 991.98px) {
  .navbar-toggler.mobile-menu-btn:focus {
    box-shadow: none !important;
    outline: none !important;
  }

  /* 覆盖focus-visible样式 */
  .navbar-toggler.mobile-menu-btn:focus-visible {
    outline: none !important;
  }
  .navbar-collapse {
    position: absolute;
    top: 100%;
    right: 0;
    padding: 10px;
    background: white;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    border-top: 1px solid #eee;
    z-index: 1000;
    max-height: calc(100vh - 100px);
    overflow-y: auto;
    transition: all 0.3s ease;
  }
  
  .navbar-collapse:not(.show) {
    opacity: 0;
    transform: translateY(-10px);
    pointer-events: none;
  }
  
  .navbar-collapse.show {
    opacity: 1;
    transform: translateY(0);
  }
  
  .nav-item {
    margin: 0;
    border-bottom: 1px solid #f8f9fa;
    white-space: nowrap;
  }
  
  .nav-link {
    padding: 1rem !important;
    border-radius: 0;
  }
  
  .nav-link.active::after {
    left: 0;
    right: 0;
  }
  
  /* 移动端隐藏下拉菜单相关样式 */
  .dropdown.d-none.d-lg-block {
    display: none !important;
  }
  
  .nav-link.d-lg-none {
    display: block !important;
  }
}

/* 桌面端样式 */
@media (min-width: 992px) {
  .dropdown {
    position: relative;
  }
  
  .dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
  }
  
  .dropdown:hover .dropdown-menu {
    display: block;
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }
  
  .dropdown-menu {
    display: block;
    opacity: 0;
    transform: translateY(-10px);
    pointer-events: none;
    transition: all 0.3s ease;
  }
  
  .dropdown-menu.show {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }
  
  /* 桌面端隐藏移动端链接 */
  .nav-link.d-lg-none {
    display: none !important;
  }
  
  .dropdown.d-none.d-lg-block {
    display: block !important;
  }
}

/* 确保路由链接样式正确 */
a.router-link-active {
  text-decoration: none;
}

a.router-link-active.router-link-exact-active {
  text-decoration: none;
}
</style>